import {
  LIVE_RELAY_DEFAULT_HTTP_ORIGIN,
  getCreateRoomApiUrlFromOrigin,
} from '../shared/liveProtocol';

export const GO_LIVE_HELPER_STATUS_PATH = '/api/helper/status';
export const GO_LIVE_HELPER_LAUNCH_SCHEME = 'lowerthirds-golive';
export const GO_LIVE_HELPER_PROTOCOL = `${GO_LIVE_HELPER_LAUNCH_SCHEME}://start`;
export const GO_LIVE_HELPER_WINDOWS_ZIP_ENV_URL = process.env.REACT_APP_GOLIVE_HELPER_WINDOWS_ZIP_URL || '';
export const GO_LIVE_HELPER_MAC_ARM64_ZIP_ENV_URL = process.env.REACT_APP_GOLIVE_HELPER_MAC_ARM64_ZIP_URL || '';
export const GO_LIVE_HELPER_MAC_X64_ZIP_ENV_URL = process.env.REACT_APP_GOLIVE_HELPER_MAC_X64_ZIP_URL || '';
export const GO_LIVE_HELPER_MAC_ZIP_ENV_URL = process.env.REACT_APP_GOLIVE_HELPER_MAC_ZIP_URL || '';
export const GO_LIVE_HELPER_ZIP_ENV_URL = process.env.REACT_APP_GOLIVE_HELPER_ZIP_URL || '';
export const GO_LIVE_HELPER_DOWNLOAD_ENV_URL = process.env.REACT_APP_GOLIVE_HELPER_DOWNLOAD_URL || '';
export const GO_LIVE_HELPER_DOWNLOAD_MODE = 'helper-download';
export const LOCAL_HELPER_PROBE_TIMEOUT_MS = 1500;
export const LOCAL_HELPER_START_POLL_INTERVAL_MS = 500;
export const LOCAL_HELPER_START_POLL_ATTEMPTS = 10;
export const LOCAL_ROOM_CREATE_TIMEOUT_MS = 5000;
export const LOCAL_HELPER_PLATFORMS = {
  WINDOWS: 'windows',
  MAC: 'mac',
  OTHER: 'other',
};
export const LOCAL_HELPER_DOWNLOAD_TARGETS = {
  WINDOWS: 'windows',
  MAC_ARM64: 'mac-arm64',
  MAC_X64: 'mac-x64',
};

export const LOCAL_REQUEST_ERROR_CODES = {
  TIMEOUT: 'TIMEOUT',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  NETWORK_UNREACHABLE: 'NETWORK_UNREACHABLE',
  PROTOCOL_HANDLER_UNAVAILABLE: 'PROTOCOL_HANDLER_UNAVAILABLE',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  UNKNOWN: 'UNKNOWN',
};

const getDefaultLocationLike = () => (
  typeof window === 'undefined' ? null : window.location
);

const getDefaultDocumentLike = () => (
  typeof document === 'undefined' ? null : document
);

const getDefaultNavigatorLike = () => (
  typeof navigator === 'undefined' ? null : navigator
);

const getLocationBase = (locationLike = getDefaultLocationLike()) => {
  if (!locationLike) {
    return {
      origin: '',
      pathname: '/',
    };
  }
  return {
    origin: locationLike.origin || '',
    pathname: locationLike.pathname || '/',
  };
};

const getDefaultFetchImpl = () => (
  typeof fetch === 'function' ? fetch : null
);

const safeParseUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    return new URL(value.trim());
  } catch {
    return null;
  }
};

const resolveAbsoluteUrl = (value, origin = '') => {
  const parsed = safeParseUrl(value);
  if (parsed) return parsed.toString();
  if (typeof value !== 'string' || !value.trim() || !origin) return '';

  try {
    return new URL(value.trim(), origin).toString();
  } catch {
    return '';
  }
};

const createLocalRequestError = (code, message, cause = null) => {
  const error = new Error(message);
  error.code = code;
  if (cause) error.cause = cause;
  return error;
};

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const normalizeHostname = (value = '') => (
  String(value || '')
    .trim()
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .toLowerCase()
);

const isIpv4Address = (value = '') => /^\d{1,3}(\.\d{1,3}){3}$/.test(value);

const isPrivateIpv4Address = (value = '') => {
  if (!isIpv4Address(value)) return false;
  const octets = value.split('.').map((segment) => Number(segment));
  if (octets.some((segment) => !Number.isInteger(segment) || segment < 0 || segment > 255)) return false;

  if (octets[0] === 10) return true;
  if (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) return true;
  if (octets[0] === 192 && octets[1] === 168) return true;
  if (octets[0] === 169 && octets[1] === 254) return true;
  return false;
};

const normalizePlatformCandidate = (value = '') => String(value || '').toLowerCase();

export const getLocalHelperPlatform = (navigatorLike = getDefaultNavigatorLike()) => {
  const platformHint = normalizePlatformCandidate(
    navigatorLike?.userAgentData?.platform
      || navigatorLike?.platform
      || navigatorLike?.userAgent
      || '',
  );

  if (platformHint.includes('mac')) return LOCAL_HELPER_PLATFORMS.MAC;
  if (platformHint.includes('win')) return LOCAL_HELPER_PLATFORMS.WINDOWS;
  return LOCAL_HELPER_PLATFORMS.OTHER;
};

export const getLocalHelperAppName = (platform = getLocalHelperPlatform()) => (
  platform === LOCAL_HELPER_PLATFORMS.MAC ? 'GoLive Helper.app' : 'GoLive Helper.exe'
);

export const getLocalHelperOsLabel = (platform = getLocalHelperPlatform()) => {
  if (platform === LOCAL_HELPER_PLATFORMS.MAC) return 'Mac';
  if (platform === LOCAL_HELPER_PLATFORMS.WINDOWS) return 'Windows';
  return 'desktop';
};

export const getGoLiveHelperProtocolUrl = (launchScheme = GO_LIVE_HELPER_LAUNCH_SCHEME) => {
  const normalizedScheme = String(launchScheme || GO_LIVE_HELPER_LAUNCH_SCHEME)
    .trim()
    .replace(/:\/+$/, '')
    .replace(/:$/, '');
  return `${normalizedScheme || GO_LIVE_HELPER_LAUNCH_SCHEME}://start`;
};

const getLocalHelperNotRunningMessage = (platform = getLocalHelperPlatform()) => (
  platform === LOCAL_HELPER_PLATFORMS.MAC
    ? 'The GoLive Helper is not running on this computer. Download the appropriate Mac ZIP, extract it, and open GoLive Helper.app, then try again.'
    : 'The GoLive Helper is not running on this computer. Download the Windows ZIP, extract it, and run GoLive Helper.exe, then try again.'
);

const getProtocolHandlerUnavailableMessage = (platform = getLocalHelperPlatform()) => (
  platform === LOCAL_HELPER_PLATFORMS.MAC
    ? 'The browser could not start the GoLive Helper automatically. Open GoLive Helper.app manually first. If macOS blocks it, go to System Settings > Privacy & Security and choose Open Anyway, then try Start Helper again.'
    : 'The browser could not start the GoLive Helper automatically. Run GoLive Helper.exe manually first so Windows can register it, then try Start Helper again. If you are testing from the repo with npm run helper:dev, keep it running manually.'
);

export const getTargetAddressSpaceForHttpOrigin = (httpOrigin = '') => {
  const parsed = safeParseUrl(httpOrigin);
  if (!parsed) return '';

  const hostname = normalizeHostname(parsed.hostname);
  if (!hostname) return '';
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') return 'loopback';
  if (isPrivateIpv4Address(hostname) || hostname.endsWith('.local')) return 'local';
  return '';
};

const buildLocalFetchInit = (url, init = {}) => {
  const targetAddressSpace = init.targetAddressSpace || getTargetAddressSpaceForHttpOrigin(url);
  return targetAddressSpace
    ? {
      ...init,
      targetAddressSpace,
    }
    : { ...init };
};

const fetchWithTimeout = async (
  url,
  init = {},
  timeoutMs = LOCAL_HELPER_PROBE_TIMEOUT_MS,
  fetchImpl = getDefaultFetchImpl(),
  platform = getLocalHelperPlatform(),
) => {
  if (typeof fetchImpl !== 'function') {
    throw createLocalRequestError(
      LOCAL_REQUEST_ERROR_CODES.UNKNOWN,
      'Fetch is not available in this environment.',
    );
  }

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timerId = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  try {
    return await fetchImpl(url, {
      ...buildLocalFetchInit(url, init),
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch (error) {
    throw classifyLocalRequestError(error, { platform });
  } finally {
    if (timerId) clearTimeout(timerId);
  }
};

export const classifyLocalRequestError = (
  error,
  { platform = getLocalHelperPlatform() } = {},
) => {
  if (error?.code && Object.values(LOCAL_REQUEST_ERROR_CODES).includes(error.code)) return error;

  if (error?.name === 'AbortError') {
    return createLocalRequestError(
      LOCAL_REQUEST_ERROR_CODES.TIMEOUT,
      'The GoLive Helper did not respond in time.',
      error,
    );
  }

  const message = String(error?.message || '').toLowerCase();
  const causeCode = String(error?.cause?.code || error?.code || '').toUpperCase();

  if (
    causeCode === 'ECONNREFUSED'
    || causeCode === 'ERR_CONNECTION_REFUSED'
    || causeCode === 'ENOTFOUND'
    || causeCode === 'ERR_NAME_NOT_RESOLVED'
    || causeCode === 'EHOSTUNREACH'
    || causeCode === 'ERR_ADDRESS_UNREACHABLE'
  ) {
    return createLocalRequestError(
      LOCAL_REQUEST_ERROR_CODES.NETWORK_UNREACHABLE,
      getLocalHelperNotRunningMessage(platform),
      error,
    );
  }

  if (
    error?.name === 'SecurityError'
    || message.includes('local network')
    || message.includes('permission')
    || message.includes('blocked')
    || message.includes('target ip address space')
    || message.includes('private network')
    || message.includes('access-control')
    || message.includes('cors')
  ) {
    return createLocalRequestError(
      LOCAL_REQUEST_ERROR_CODES.PERMISSION_DENIED,
      'Your browser blocked access to the GoLive Helper. Check local network access in the browser and try again.',
      error,
    );
  }

  if (
    message.includes('failed to fetch')
    || message.includes('load failed')
    || message.includes('networkerror')
    || message.includes('fetch')
  ) {
    return createLocalRequestError(
      LOCAL_REQUEST_ERROR_CODES.NETWORK_UNREACHABLE,
      getLocalHelperNotRunningMessage(platform),
      error,
    );
  }

  return createLocalRequestError(
    LOCAL_REQUEST_ERROR_CODES.UNKNOWN,
    error?.message || 'Unable to reach the GoLive Helper.',
    error,
  );
};

export const getGoLiveHelperDownloadUrl = (
  locationLike = getDefaultLocationLike(),
) => {
  const { origin, pathname } = getLocationBase(locationLike);
  const base = origin ? `${origin}${pathname}` : pathname || '/';
  const url = new URL(base, origin || 'http://localhost');
  url.search = '';
  url.hash = '';
  url.searchParams.set('mode', GO_LIVE_HELPER_DOWNLOAD_MODE);
  if (origin) return url.toString();
  return `${url.pathname}?${url.searchParams.toString()}`;
};

export const getGoLiveHelperDownloadTargets = ({
  locationLike = getDefaultLocationLike(),
  windowsEnvUrl = GO_LIVE_HELPER_WINDOWS_ZIP_ENV_URL,
  macArm64EnvUrl = GO_LIVE_HELPER_MAC_ARM64_ZIP_ENV_URL,
  macX64EnvUrl = GO_LIVE_HELPER_MAC_X64_ZIP_ENV_URL,
  macEnvUrl = GO_LIVE_HELPER_MAC_ZIP_ENV_URL,
  legacyEnvUrl = GO_LIVE_HELPER_ZIP_ENV_URL || GO_LIVE_HELPER_DOWNLOAD_ENV_URL,
} = {}) => {
  const { origin } = getLocationBase(locationLike);
  const windowsUrl = resolveAbsoluteUrl(windowsEnvUrl, origin)
    || resolveAbsoluteUrl(legacyEnvUrl, origin);
  const legacyMacUrl = resolveAbsoluteUrl(macEnvUrl, origin);
  const macArm64Url = resolveAbsoluteUrl(macArm64EnvUrl, origin) || legacyMacUrl;
  const macX64Url = resolveAbsoluteUrl(macX64EnvUrl, origin) || legacyMacUrl;

  return {
    windows: {
      platform: LOCAL_HELPER_PLATFORMS.WINDOWS,
      label: 'Windows',
      url: windowsUrl,
      isConfigured: Boolean(windowsUrl),
      envVarName: 'REACT_APP_GOLIVE_HELPER_WINDOWS_ZIP_URL',
      legacyEnvVarNames: ['REACT_APP_GOLIVE_HELPER_ZIP_URL', 'REACT_APP_GOLIVE_HELPER_DOWNLOAD_URL'],
    },
    macArm64: {
      platform: LOCAL_HELPER_DOWNLOAD_TARGETS.MAC_ARM64,
      label: 'Mac (Silicon)',
      url: macArm64Url,
      isConfigured: Boolean(macArm64Url),
      envVarName: 'REACT_APP_GOLIVE_HELPER_MAC_ARM64_ZIP_URL',
      legacyEnvVarNames: ['REACT_APP_GOLIVE_HELPER_MAC_ZIP_URL'],
    },
    macX64: {
      platform: LOCAL_HELPER_DOWNLOAD_TARGETS.MAC_X64,
      label: 'Mac (Intel)',
      url: macX64Url,
      isConfigured: Boolean(macX64Url),
      envVarName: 'REACT_APP_GOLIVE_HELPER_MAC_X64_ZIP_URL',
      legacyEnvVarNames: ['REACT_APP_GOLIVE_HELPER_MAC_ZIP_URL'],
    },
  };
};

export const getGoLiveHelperZipUrl = (
  locationLike = getDefaultLocationLike(),
  platformOrWindowsEnvUrl = getLocalHelperPlatform(),
  windowsEnvUrl = GO_LIVE_HELPER_WINDOWS_ZIP_ENV_URL,
  macArm64EnvUrl = GO_LIVE_HELPER_MAC_ARM64_ZIP_ENV_URL,
  macX64EnvUrl = GO_LIVE_HELPER_MAC_X64_ZIP_ENV_URL,
  macEnvUrl = GO_LIVE_HELPER_MAC_ZIP_ENV_URL,
  legacyEnvUrl = GO_LIVE_HELPER_ZIP_ENV_URL || GO_LIVE_HELPER_DOWNLOAD_ENV_URL,
) => {
  const platformValues = Object.values(LOCAL_HELPER_PLATFORMS);
  const usesPlatformArgument = platformValues.includes(platformOrWindowsEnvUrl);
  const platform = usesPlatformArgument ? platformOrWindowsEnvUrl : getLocalHelperPlatform();
  const resolvedWindowsEnvUrl = usesPlatformArgument ? windowsEnvUrl : platformOrWindowsEnvUrl;
  const targets = getGoLiveHelperDownloadTargets({
    locationLike,
    windowsEnvUrl: resolvedWindowsEnvUrl,
    macArm64EnvUrl,
    macX64EnvUrl,
    macEnvUrl,
    legacyEnvUrl,
  });

  if (platform === LOCAL_HELPER_PLATFORMS.MAC) {
    return targets.macArm64.url || targets.macX64.url || targets.windows.url;
  }

  return targets.windows.url || targets.macArm64.url || targets.macX64.url;
};

export const getSafeLocalLaunchRelayOrigin = ({
  preferredRelayHttpOrigin = '',
  loopbackHttpOrigin = LIVE_RELAY_DEFAULT_HTTP_ORIGIN,
  locationLike = getDefaultLocationLike(),
} = {}) => {
  const loopbackParsed = safeParseUrl(loopbackHttpOrigin);
  const preferredParsed = safeParseUrl(preferredRelayHttpOrigin);
  const pageOrigin = safeParseUrl(locationLike?.origin || '');
  const fallbackOrigin = loopbackParsed?.origin || LIVE_RELAY_DEFAULT_HTTP_ORIGIN;

  if (!preferredParsed) return fallbackOrigin;
  if (!pageOrigin || pageOrigin.protocol !== 'https:') return preferredParsed.origin;
  if (preferredParsed.protocol === 'https:') return preferredParsed.origin;

  const targetAddressSpace = getTargetAddressSpaceForHttpOrigin(preferredParsed.origin);
  return targetAddressSpace === 'loopback' ? preferredParsed.origin : fallbackOrigin;
};

const normalizeHelperStatus = (payload = {}, fallbackOrigin = LIVE_RELAY_DEFAULT_HTTP_ORIGIN) => {
  const loopbackParsed = safeParseUrl(payload.loopbackHttpOrigin);
  const preferredParsed = safeParseUrl(payload.preferredRelayHttpOrigin);
  if (!payload?.ok || !loopbackParsed) {
    throw createLocalRequestError(
      LOCAL_REQUEST_ERROR_CODES.INVALID_RESPONSE,
      'The GoLive Helper returned an invalid status response.',
    );
  }

  return {
    ok: true,
    version: typeof payload.version === 'string' ? payload.version : '0.0.0',
    loopbackHttpOrigin: loopbackParsed.origin,
    preferredRelayHttpOrigin: preferredParsed ? preferredParsed.origin : loopbackParsed.origin || fallbackOrigin,
    lanEnabled: payload.lanEnabled === true,
    launchScheme: typeof payload.launchScheme === 'string' && payload.launchScheme.trim()
      ? payload.launchScheme.trim()
      : GO_LIVE_HELPER_LAUNCH_SCHEME,
  };
};

export const requestLocalHelperStatus = async ({
  httpOrigin = LIVE_RELAY_DEFAULT_HTTP_ORIGIN,
  timeoutMs = LOCAL_HELPER_PROBE_TIMEOUT_MS,
  fetchImpl = getDefaultFetchImpl(),
  platform = getLocalHelperPlatform(),
} = {}) => {
  const response = await fetchWithTimeout(
    new URL(GO_LIVE_HELPER_STATUS_PATH, httpOrigin).toString(),
    { method: 'GET' },
    timeoutMs,
    fetchImpl,
    platform,
  );

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw createLocalRequestError(
      LOCAL_REQUEST_ERROR_CODES.INVALID_RESPONSE,
      payload?.message || 'The GoLive Helper could not report its status.',
    );
  }

  return normalizeHelperStatus(payload, httpOrigin);
};

export const launchLocalHelper = (
  protocolUrl = GO_LIVE_HELPER_PROTOCOL,
  documentLike = getDefaultDocumentLike(),
  platform = getLocalHelperPlatform(),
) => {
  if (!documentLike?.body || typeof documentLike.createElement !== 'function') return false;

  if (platform === LOCAL_HELPER_PLATFORMS.MAC) {
    const link = documentLike.createElement('a');
    link.style.display = 'none';
    link.setAttribute('aria-hidden', 'true');
    link.href = protocolUrl;
    documentLike.body.appendChild(link);
    if (typeof link.click === 'function') {
      link.click();
    }
    setTimeout(() => {
      try {
        link.remove();
      } catch {
        // Ignore cleanup failures for detached protocol elements.
      }
    }, 1000);
    return true;
  }

  const frame = documentLike.createElement('iframe');
  frame.style.display = 'none';
  frame.setAttribute('aria-hidden', 'true');
  frame.src = protocolUrl;
  documentLike.body.appendChild(frame);
  setTimeout(() => {
    try {
      frame.remove();
    } catch {
      // Ignore cleanup failures for detached protocol frames.
    }
  }, 1000);
  return true;
};

export const waitForLocalHelper = async ({
  httpOrigin = LIVE_RELAY_DEFAULT_HTTP_ORIGIN,
  attempts = LOCAL_HELPER_START_POLL_ATTEMPTS,
  intervalMs = LOCAL_HELPER_START_POLL_INTERVAL_MS,
  fetchImpl = getDefaultFetchImpl(),
  platform = getLocalHelperPlatform(),
} = {}) => {
  let lastError = null;
  for (let index = 0; index < attempts; index += 1) {
    try {
      return await requestLocalHelperStatus({ httpOrigin, fetchImpl, platform });
    } catch (error) {
      lastError = error;
      if (index < attempts - 1) await sleep(intervalMs);
    }
  }
  throw lastError || createLocalRequestError(
    LOCAL_REQUEST_ERROR_CODES.NETWORK_UNREACHABLE,
    getLocalHelperNotRunningMessage(platform),
  );
};

export const ensureLocalHelperReady = async ({
  httpOrigin = LIVE_RELAY_DEFAULT_HTTP_ORIGIN,
  fetchImpl = getDefaultFetchImpl(),
  startIfNeeded = true,
  forceProtocolLaunch = false,
  launchScheme = GO_LIVE_HELPER_LAUNCH_SCHEME,
  onStateChange = null,
  documentLike = getDefaultDocumentLike(),
  attempts = LOCAL_HELPER_START_POLL_ATTEMPTS,
  intervalMs = LOCAL_HELPER_START_POLL_INTERVAL_MS,
  platform = getLocalHelperPlatform(),
} = {}) => {
  onStateChange?.('checking-helper');
  try {
    return await requestLocalHelperStatus({ httpOrigin, fetchImpl, platform });
  } catch (error) {
    const lastError = classifyLocalRequestError(error, { platform });
    if (!startIfNeeded && !forceProtocolLaunch) throw lastError;
    onStateChange?.('starting-helper');
    const attemptedLaunch = launchLocalHelper(
      getGoLiveHelperProtocolUrl(launchScheme),
      documentLike,
      platform,
    );
    try {
      return await waitForLocalHelper({ httpOrigin, fetchImpl, attempts, intervalMs, platform });
    } catch (waitError) {
      const classifiedError = classifyLocalRequestError(waitError?.cause || waitError || lastError, { platform });
      if (
        attemptedLaunch
        && (forceProtocolLaunch
          || classifiedError.code === LOCAL_REQUEST_ERROR_CODES.NETWORK_UNREACHABLE
          || classifiedError.code === LOCAL_REQUEST_ERROR_CODES.UNKNOWN)
      ) {
        throw createLocalRequestError(
          LOCAL_REQUEST_ERROR_CODES.PROTOCOL_HANDLER_UNAVAILABLE,
          getProtocolHandlerUnavailableMessage(platform),
          classifiedError,
        );
      }
      throw classifiedError;
    }
  }
};

export const createLocalHelperRoom = async ({
  project,
  playlistItems,
  previewItemId,
  loopbackHttpOrigin = LIVE_RELAY_DEFAULT_HTTP_ORIGIN,
  fetchImpl = getDefaultFetchImpl(),
  timeoutMs = LOCAL_ROOM_CREATE_TIMEOUT_MS,
  platform = getLocalHelperPlatform(),
} = {}) => {
  const response = await fetchWithTimeout(
    getCreateRoomApiUrlFromOrigin(loopbackHttpOrigin),
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        project,
        playlistItems,
        previewItemId,
      }),
    },
    timeoutMs,
    fetchImpl,
    platform,
  );

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || !payload?.roomId || !payload?.controlToken) {
    const error = createLocalRequestError(
      payload?.error || LOCAL_REQUEST_ERROR_CODES.INVALID_RESPONSE,
      payload?.message || 'Unable to create a local live room right now.',
    );
    error.response = payload;
    throw error;
  }

  return payload;
};
